import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import {
  usePortfolioOverview,
  useProfitLoss,
  useTransactions,
} from '@/features/portfolio/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState, savedAtMeta } from '@/lib/view-state'
import AddTransactionModal from '@/screens/portfolio/components/add-transaction/AddTransactionModal'
import type { TransactionEditTarget } from '@/screens/portfolio/components/add-transaction/editTarget'
import ActivityLedger from '@/screens/portfolio/components/activity/ActivityLedger'
import ActivitySummaryCards from '@/screens/portfolio/components/activity/ActivitySummaryCards'
import DeleteTransactionDialog from '@/screens/portfolio/components/activity/DeleteTransactionDialog'
import TransactionDetailDialog from '@/screens/portfolio/components/activity/TransactionDetailDialog'
import {
  findEditTarget,
  mergeTransactions,
  type ActivityRow,
} from '@/screens/portfolio/components/activity/mergeTransactions'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio/activity')({
  // `?symbol=` — the Holdings row menu's "Edit transactions" arrives here
  // with this, exactly the shape `/alerts?symbol=` already uses for the same
  // "carry a preset across a navigation" purpose. Seeds `ActivityLedger`'s
  // own search box; nothing else on this screen reads it, and it is not kept
  // in sync afterwards (typing in that box is local state, same as every
  // other Portfolio filter).
  validateSearch: z.object({ symbol: z.string().optional() }),
  component: ActivityScreen,
})

/**
 * Portfolio · Activity (V2 `4236:76802`, V1 997:27916 plus 3448:36251
 * "Month filter open").
 *
 * Backed by ONE gating read, `GET /transactions/` — a single object with four
 * separate arrays (`purchases`, `sells`, `bonusShares`, `cashDividends`), not
 * a ledger. `mergeTransactions` (co-located, pure, and separately tested) is
 * the one place those four shapes become a single `ActivityRow[]`; this
 * screen hands the merged rows to `ActivityLedger` for its tabs, search,
 * month filter and pager.
 *
 * ── The summary row reads two MORE endpoints, and gates nothing ────────────
 *
 * V2's 6 summary cards (`5191:96397`) need figures `/transactions/` does not
 * carry — current holdings and their totals, and realised P/L — so
 * `usePortfolioOverview`/`useProfitLoss` are fetched alongside the ledger,
 * exactly the way `HoldingsScreen` reads them for `BookTotals`. Neither joins
 * the page's own gate: a slow or failing pair shows its own tiles as a
 * skeleton or an em-dash (`ActivitySummaryCards`'s own `Gated`) rather than
 * blanking a ledger that is already in hand.
 *
 * ── What is gated, and why ────────────────────────────────────────────────
 *
 * - Per-lot "Realised P/L" on a historical sell: no such field exists
 *   anywhere in the contract — `sells[]` carries none, and `/profit-loss/`
 *   only ever gives a cumulative per-symbol figure.
 * - "Price not recorded / Add price" flow: structurally impossible, not
 *   merely unbuilt — `price`/`rate` is a required field on `POST
 *   /transactions/buy/` (`buyRowSchema.rate` is never null), so no lot can
 *   exist without one. Reported rather than built against a state the
 *   contract cannot produce.
 * - "Position after" (running quantity and average cost per row): no field,
 *   and computing it client-side would mean replaying the backend's own WACC
 *   engine and risking drift from its numbers — see BACKEND CHANGES NEEDED.
 *
 * ── Row menu: Edit and Delete both wired now ─────────────────────────────────
 *
 * `DELETE /transactions/{group}/{id}/` names one row by its own id and group,
 * through `DeleteTransactionDialog` (one instance, mounted here, the same
 * "one dialog, whichever row opened it" shape `HoldingsScreen`'s alert modal
 * uses). "Edit" opens the SAME `AddTransactionModal` Holdings' "Add
 * transaction" opens (a second, independent mount, its own state) with
 * `editTarget` instead of `presetSymbol` — that prop needs the row exactly as
 * `useTransactions()` returned it, not this screen's flattened `ActivityRow`,
 * so `findEditTarget` re-finds it by group and id (`mergeTransactions.ts`;
 * see `SCRATCH/api/PF-WP3-props.md`).
 *
 * ── A row opens its entry; a search can name a company ───────────────────────
 *
 * The 2026-11-03 mock: a click on a row opens `TransactionDetailDialog` (one instance, here, as
 * the delete dialog), whose "Edit entry" is the row menu's Edit. A search that names one company
 * draws "How you handled X" over the ledger (`SymbolInsightsCard`), from the same ledger plus the
 * overview's holdings and `/profit-loss/`'s row for it — the two reads the summary strip already
 * makes, so it adds no request.
 *
 * ── V2 ────────────────────────────────────────────────────────────────────
 *
 * Same content as V1 — chip strip, month picker, the BS-month-grouped
 * ledger, the footer count — recomposed: the ledger is now ONE table panel
 * with column heads, month bands and a real pager (see
 * `ActivityLedger`/`ActivityRowView`), the body sits in `ScreenLayout`'s main
 * column beside the shared market rail, and the family header is mounted
 * ONCE by the layout.
 *
 * The summary strip stays, though the revamp mock draws none: the owner asked
 * for it on 2026-10-23 and the rulings of record beat the mock (PLAN D5). The
 * mock's closing Mitra banner is gone, as on every Portfolio page.
 */
function ActivityScreen() {
  const shareholderId = useActiveShareholderId()
  const { symbol } = Route.useSearch()
  const tx = useTransactions(shareholderId)
  const overviewQuery = usePortfolioOverview(shareholderId)
  const profitLossQuery = useProfitLoss(shareholderId)
  const online = useOnline()
  const view = resolveViewState(tx, {
    isEmpty: (data) =>
      data.purchases.length === 0 &&
      data.sells.length === 0 &&
      data.bonusShares.length === 0 &&
      data.cashDividends.length === 0,
    online,
  })

  const [deleteTarget, setDeleteTarget] = useState<ActivityRow | null>(null)
  const [editTarget, setEditTarget] = useState<TransactionEditTarget | null>(null)
  const [detailRow, setDetailRow] = useState<ActivityRow | null>(null)
  const holdings = overviewQuery.data?.holdings ?? []

  const editRow = (row: ActivityRow) => {
    const data = tx.data
    if (data === undefined) return
    const target = findEditTarget(data, row.group, row.id)
    if (target !== null) setEditTarget(target)
  }

  return (
    <ScreenLayout
      header={<PortfolioFamilyHeader tab="activity" compactAction />}
      pageTitle="Activity"
    >
      {view === 'loading' ? (
        <>
          {/* The loaded page's shape: the totals strip (six tiles, 3 × 2 at the 844px column,
              ~200px), then the tabs row and table 16px apart as the ledger draws them. */}
          <Skeleton className="h-50 w-full rounded-lg" />
          <div className="flex flex-col gap-4">
            <Skeleton className="h-9 w-full rounded-md" />
            <DataTableSkeleton rows={8} columns={5} rowHeight="lg" />
          </div>
        </>
      ) : view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          // Offline keeps EmptyState's own "You are offline" copy — see the
          // matching comment in HoldingsScreen.
          {...(view === 'failed'
            ? {
                title: 'Activity did not load',
                description: failureMessage(portfolioFailure(tx.error) ?? { kind: 'failed' }),
              }
            : {})}
          onRetry={() => {
            void tx.refetch()
          }}
          retrying={tx.isFetching}
          meta={
            view === 'failed' ? lastUpdatedMeta(tx.dataUpdatedAt) : savedAtMeta(tx.dataUpdatedAt)
          }
        />
      ) : view === 'empty' ? (
        <EmptyState
          kind="empty"
          size="page"
          title="No activity recorded yet"
          description="Every buy, sell, bonus and dividend you record will show up here."
        />
      ) : (
        (() => {
          // Bound once as a `const` rather than read repeatedly off `tx.data`:
          // the callbacks below close over `data`, and a `const` keeps
          // TypeScript's narrowing (this branch only runs once it is defined)
          // inside those closures too, which a repeated `tx.data` access would not.
          const data = tx.data
          if (data === undefined) return null

          return (
            <>
              <ActivitySummaryCards
                purchases={data.purchases}
                sells={data.sells}
                overview={overviewQuery.data}
                overviewPending={overviewQuery.isPending}
                overviewError={overviewQuery.isError}
                profitLoss={profitLossQuery.data}
                profitLossPending={profitLossQuery.isPending}
                profitLossError={profitLossQuery.isError}
              />

              <ActivityLedger
                rows={mergeTransactions(data)}
                initialQuery={symbol ?? ''}
                onEdit={editRow}
                onDeleteRequest={setDeleteTarget}
                onOpen={setDetailRow}
                holdings={holdings}
                profitLoss={profitLossQuery.data?.rows ?? []}
              />
            </>
          )
        })()
      )}

      <TransactionDetailDialog
        row={detailRow}
        companyName={
          holdings.find((holding) => holding.symbol === detailRow?.symbol)?.companyName ?? null
        }
        onOpenChange={(open) => {
          if (!open) setDetailRow(null)
        }}
        onEdit={editRow}
      />

      <DeleteTransactionDialog
        row={deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      />

      <AddTransactionModal
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null)
        }}
        {...(editTarget === null ? {} : { editTarget })}
      />
    </ScreenLayout>
  )
}
